import { Link } from 'react-router-dom';
import {
  ShieldCheck, Target, Lightbulb, Layers, Users, Building2, Cpu, Quote,
  ArrowRight, Mail, ChevronRight,
} from 'lucide-react';
import InkPhDLogo from '@/components/InkPhDLogo';
import SeoHead from '@/components/SeoHead';
import AppFooter from '@/components/AppFooter';
import { LEGAL_CONFIG } from '@/legal/config';

const C = LEGAL_CONFIG.controller;

const PILLARS = [
  ['Retrieval before generation',
   'Nothing is written from the model\'s memory. Every paragraph is assembled from passages retrieved out of the library you built — your PDFs, your PubMed imports, your Zotero collection — and the passages travel with the text as provenance.'],
  ['Numbered citations that resolve',
   'Citations are emitted as strict Vancouver numbers and each one is resolved against Crossref or PubMed. A reference that cannot be found is flagged, never quietly formatted into something that looks real.'],
  ['Claims are scored, not trusted',
   'Each sentence is re-checked against the evidence that supposedly supports it. Unsupported statements are underlined in the editor while you write, the same way a spell-checker underlines a typo.'],
  ['Human approval is the gate',
   'No section is ever finalised automatically. You approve, the approval is recorded, and the export carries an explicit AI-disclosure line. That is what makes the output defensible in front of a committee.'],
];

const CAPABILITIES = [
  ['Document-centred workspace',
   'Three panes — section hierarchy, scientific editor, intelligence panel — for ten document types: doctoral dissertation, research article, systematic review, meta-analysis, clinical protocol, grant proposal, case report, conference abstract, monograph and editorial.'],
  ['Evidence library and discovery',
   'Upload PDFs or import from PubMed, OpenAlex, Semantic Scholar, Crossref and arXiv; sync a Zotero collection; fetch open-access full text automatically by DOI through Unpaywall and Europe PMC.'],
  ['Systematic review pipeline',
   'PRISMA 2020 checklist, deduplication, AI pre-screening and an ASReview-style active-learning queue that reorders the remaining records after every decision, with a stopping rule and a recall estimate you can report in Methods.'],
  ['Statistics on your own data',
   'Upload a dataset and generate Kaplan–Meier curves with risk tables, Cox models with a proportional-hazards check, ROC/AUC, regressions and descriptive tables — each with the methods sentence and the figure legend written for you.'],
  ['Reporting-guideline compliance',
   'Eleven EQUATOR guidelines and 426 individual items (CONSORT, STROBE, PRISMA, CARE, SPIRIT, STARD and more), audited item by item with suggested fixes.'],
  ['Consensus and citation mapping',
   'A design-weighted consensus meter shows how many studies support or refute a stated claim; the citation network map reveals the classics your own references keep citing.'],
  ['Defence preparation',
   'Five reviewer personas interrogate the manuscript — methodologist, statistician, clinician, editor and sceptic — and a readiness score tells you what a committee will attack first.'],
  ['Draft intake and writing plan',
   'Upload a manuscript you already started; the platform splits it into sections, imports its reference list, audits the gaps and turns every gap into a tracked writing task that is AI-verified when you tick it off.'],
  ['Publication-ready output',
   'DOCX with native Word fields for figure and table numbering and cross-references, plus PDF, LaTeX, Markdown and HTML, and thousands of CSL citation styles.'],
];

const AUDIENCE = [
  ['PhD candidates in medicine',
   'The people this was built for: one big manuscript, years of work, a committee waiting at the end.'],
  ['Clinical researchers and residents',
   'Case reports, cohort analyses and conference abstracts written between shifts, with the statistics done properly.'],
  ['Systematic reviewers',
   'Teams that need PRISMA discipline, screening that scales and an audit trail they can publish.'],
  ['Supervisors and research groups',
   'Shared standards across a department — the same guideline checks, the same citation verification, the same readiness score for everyone.'],
];

function Section({ id, eyebrow, title, children, testid }) {
  return (
    <section id={id} className="py-16 border-t border-slate-200 first:border-0 first:pt-0"
      data-testid={testid}>
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[hsl(212,90%,38%)]">
        {eyebrow}
      </span>
      <h2 className="font-serif text-[30px] sm:text-[34px] leading-tight mt-3 mb-6 text-slate-900"
        style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col bg-[hsl(210,24%,99%)] text-slate-900">
      <SeoHead
        title="About InkPhD — evidence-grounded infrastructure for scientific writing"
        path="/about"
        description="Why InkPhD exists, how it works and who owns it: an evidence-grounded writing platform for medical dissertations and scientific papers, built by International Sci Ink Press Ltd."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About InkPhD',
          url: 'https://inkphd.com/about',
          about: {
            '@type': 'Organization',
            name: C.legal_name,
            alternateName: 'InkPhD',
            identifier: C.registration_number,
            vatID: C.vat_number,
            email: C.contact_email,
            address: {
              '@type': 'PostalAddress',
              streetAddress: '57 Antim I Str., floor 1',
              addressLocality: 'Sofia',
              postalCode: '1303',
              addressCountry: 'BG',
            },
          },
        }} />

      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1000px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="about-home-link"><InkPhDLogo size="md" /></Link>
          <div className="flex items-center gap-5 text-[13px]">
            <Link to="/#compare" className="hidden sm:inline text-slate-700 hover:text-slate-900">Compare</Link>
            <Link to="/#pricing" className="hidden sm:inline text-slate-700 hover:text-slate-900">Pricing</Link>
            <Link to="/thesis/register"
              className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white font-medium px-3.5 py-2 rounded-md transition-colors"
              data-testid="about-cta-register">
              Start writing <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1000px] mx-auto w-full px-6 py-16" data-testid="about-page">
        <div className="mb-4 flex items-center gap-2 text-[12px] text-slate-500">
          <Link to="/" className="hover:text-slate-800">Home</Link>
          <ChevronRight className="w-3 h-3" /> About
        </div>

        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6"
          style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          A writing instrument that refuses to invent evidence.
        </h1>
        <p className="text-base sm:text-lg text-slate-600 max-w-3xl leading-relaxed">
          InkPhD is evidence-grounded infrastructure for medical dissertations and scientific
          papers. It drafts, cites, checks statistics and rehearses the defence — but it will not
          write a sentence your own sources cannot support. Everything it produces can be traced
          back, line by line, to a document you put there yourself.
        </p>

        <div className="mt-10 grid sm:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {[['10', 'document types'], ['426', 'guideline items checked'],
            ['5', 'reviewer personas at the defence']].map(([n, l]) => (
            <div key={l} className="bg-white p-5">
              <div className="font-serif text-[34px] leading-none text-slate-900">{n}</div>
              <div className="text-[12px] text-slate-500 mt-1">{l}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 space-y-0">
          <Section id="purpose" eyebrow="PURPOSE" testid="about-purpose"
            title="The purpose: make a defensible manuscript the cheapest path, not the hardest one.">
            <div className="flex gap-4">
              <Target className="w-5 h-5 text-[hsl(212,90%,38%)] shrink-0 mt-1" />
              <div className="space-y-4 text-[15px] leading-relaxed text-slate-700">
                <p>
                  A doctoral manuscript fails for boring reasons. A citation that does not say what
                  it was cited for. A survival analysis without a proportional-hazards check. A
                  Methods section missing the ethics approval number. A results table that stops
                  matching the text after the third revision. None of these are failures of
                  intelligence — they are failures of bookkeeping, spread over three years.
                </p>
                <p>
                  General-purpose AI writing tools make that worse. They are fast, fluent and
                  perfectly willing to produce a reference that does not exist. The purpose of
                  InkPhD is the opposite: to make the rigorous route the fast route, so that the
                  easiest thing to do is also the thing that survives peer review.
                </p>
              </div>
            </div>
          </Section>

          <Section id="idea" eyebrow="THE IDEA" testid="about-idea"
            title="Retrieval first, generation second — and provenance all the way through.">
            <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              {PILLARS.map(([t, b]) => (
                <div key={t} className="bg-white p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-[hsl(212,90%,38%)]" />
                    <h3 className="font-serif text-[19px] text-slate-900">{t}</h3>
                  </div>
                  <p className="text-[13.5px] leading-relaxed text-slate-600">{b}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="motivation" eyebrow="MOTIVATION" testid="about-motivation"
            title="Why it was built at all.">
            <div className="flex gap-4">
              <Quote className="w-5 h-5 text-[hsl(212,90%,38%)] shrink-0 mt-1" />
              <div className="space-y-4 text-[15px] leading-relaxed text-slate-700">
                <p>
                  InkPhD began inside a clinical practice, not a software company. It started as
                  private tooling for one medical dissertation: scripts to keep a reference list
                  honest, to recompute survival curves after every data cut, to check a chapter
                  against the reporting guideline it was supposed to follow. The writing was never
                  the bottleneck. The bookkeeping around the writing was.
                </p>
                <p>
                  The same complaints came back from every colleague: the literature is too large
                  to hold in one head, statistical software lives far from the manuscript, and the
                  language editing services that are affordable do not understand methodology.
                  Meanwhile paying someone to write the thesis is both common and corrosive — it
                  produces doctors who cannot defend their own work.
                </p>
                <p>
                  So the goal was set deliberately narrow: keep the author in charge, remove the
                  bookkeeping, and refuse to generate anything that cannot be traced to a source.
                  The platform asks more of you than a generic AI writer does — your data, your
                  sources, your judgement on every claim. That is the point. The result has to be
                  genuinely yours.
                </p>
              </div>
            </div>
          </Section>

          <Section id="system" eyebrow="THE SYSTEM" testid="about-system"
            title="What the platform actually does.">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
              {CAPABILITIES.map(([t, b]) => (
                <div key={t} className="bg-white p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4 text-[hsl(212,90%,38%)]" />
                    <h3 className="font-serif text-[17px] text-slate-900">{t}</h3>
                  </div>
                  <p className="text-[13px] leading-relaxed text-slate-600">{b}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="architecture" eyebrow="HOW IT IS BUILT" testid="about-architecture"
            title="Characteristics of the system.">
            <div className="flex gap-4">
              <Cpu className="w-5 h-5 text-[hsl(212,90%,38%)] shrink-0 mt-1" />
              <div className="space-y-3 text-[14.5px] leading-relaxed text-slate-700">
                <p>
                  <strong>Architecture.</strong> A React front end, a Python/FastAPI back end,
                  MongoDB for documents and a per-project vector index for retrieval. Each project
                  gets its own isolated index; no query ever reaches another author's material.
                </p>
                <p>
                  <strong>Retrieval.</strong> Hybrid search — dense embeddings plus lexical
                  matching — over chunked full text, with the source, page and passage kept next to
                  every retrieved snippet so provenance survives all the way into the export.
                </p>
                <p>
                  <strong>Models.</strong> Third-party general-purpose models are used through an
                  API for generation, embeddings and transcription. We train nothing on your
                  content, and your manuscripts are not submitted for model training.
                </p>
                <p>
                  <strong>Statistics.</strong> Real statistical libraries, not a language model
                  guessing numbers: survival analysis, regression, diagnostics and plots are
                  computed from your uploaded dataset and reported with the assumptions tested.
                </p>
                <p>
                  <strong>Verification.</strong> Identifiers are resolved against Crossref and
                  PubMed; open-access full text is fetched where it exists; claims are re-scored
                  against the retrieved evidence; reporting guidelines are audited item by item.
                </p>
                <p>
                  <strong>Safeguards.</strong> Research Use Only. Not a medical device, not
                  clinical decision support, no diagnosis and no treatment recommendations. AI
                  involvement is always explicit, every generated section is labelled, and the
                  author's approval is recorded as metadata.
                </p>
                <p>
                  <strong>Data protection.</strong> GDPR by design: self-service export and
                  permanent erasure, a published subprocessor list, an Article 28 DPA for
                  institutions, strictly necessary storage only — no advertising, no analytics, no
                  cross-site tracking.
                </p>
              </div>
            </div>
          </Section>

          <Section id="audience" eyebrow="WHO IT IS FOR" testid="about-audience"
            title="Built for the people who have to defend the text.">
            <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              {AUDIENCE.map(([t, b]) => (
                <div key={t} className="bg-white p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-[hsl(212,90%,38%)]" />
                    <h3 className="font-serif text-[18px] text-slate-900">{t}</h3>
                  </div>
                  <p className="text-[13.5px] leading-relaxed text-slate-600">{b}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[13.5px] text-slate-600 leading-relaxed max-w-3xl">
              The interface is in English; generated content can be produced in twelve languages,
              including Bulgarian, so you can write for a local committee and publish
              internationally from the same project.
            </p>
          </Section>

          <Section id="ownership" eyebrow="OWNERSHIP" testid="about-ownership"
            title="Who stands behind it.">
            <div className="border border-slate-200 bg-white p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <Building2 className="w-5 h-5 text-[hsl(212,90%,38%)] shrink-0 mt-1" />
                <div className="space-y-4 text-[14.5px] leading-relaxed text-slate-700">
                  <p>
                    InkPhD is owned and operated by <strong>{C.legal_name}</strong> — an
                    independent academic publisher founded in Sofia, Bulgaria in 2018 by
                    Dr Ivan Inkov, a thoracic surgeon who still runs its editorial desk.
                  </p>
                  <p>
                    The house publishes two peer-reviewed, open-access titles: the International
                    Journal of Medical Reviews and Case Reports and the International Journal of
                    Surgery and Medicine. Between them they have registered roughly nine thousand
                    Crossref DOIs, are curated by an editorial network of more than 180 members
                    across 40 countries, and are archived for the long term in Portico and
                    CLOCKSS. Every submission is screened for plagiarism, held to COPE, ICMJE and
                    Declaration of Helsinki standards, and required to follow the EQUATOR
                    reporting guideline that matches its study design.
                  </p>
                  <p>
                    That is the origin of this platform. Eight years of desk decisions produce a
                    very clear list of why good research gets rejected: a citation that does not
                    support the sentence, a survival analysis without its assumptions tested, a
                    Methods section missing its ethics statement, a reporting checklist quietly
                    ignored. InkPhD is that list turned into software — the editorial standard
                    applied while the manuscript is being written, instead of months later in a
                    reviewer's report.
                  </p>
                  <p className="text-[13px] text-slate-600">
                    The publisher also maintains other scientific-infrastructure projects; InkPhD
                    is the writing instrument in that family. The platform, its source code and
                    the InkPhD brand are the property of {C.legal_name}. Your manuscripts,
                    datasets and uploaded literature remain entirely yours — we claim no rights
                    over what you create and we do not use it to train models.
                  </p>
                  <p className="text-[12.5px] text-slate-500">
                    Registered in Sofia, Bulgaria (EU) · company number {C.registration_number}.
                    Full commercial and tax details are on the{' '}
                    <Link to="/legal/billing" className="underline hover:text-slate-800">
                      billing page
                    </Link>, contact addresses in the{' '}
                    <Link to="/legal/privacy" className="underline hover:text-slate-800">
                      privacy policy
                    </Link>.
                  </p>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-2 text-[12px]">
                {[['Terms of Use', '/legal/terms'], ['Privacy', '/legal/privacy'],
                  ['EU AI Act', '/legal/ai-act'], ['DPA (Art. 28)', '/legal/dpa'],
                  ['Subprocessors', '/legal/subprocessors'],
                  ['Billing & invoices', '/legal/billing'],
                  ['Research Use Only', '/legal/ruo']].map(([label, href]) => (
                  <Link key={href} to={href}
                    className="inline-flex items-center gap-1 px-2.5 py-1 border border-slate-200 hover:border-[hsl(212,90%,55%)] hover:text-[hsl(212,90%,38%)] transition-colors"
                    data-testid={`about-legal-${href.split('/').pop()}`}>
                    <ShieldCheck className="w-3 h-3" /> {label}
                  </Link>
                ))}
              </div>
            </div>
          </Section>

          <Section id="contact" eyebrow="CONTACT" testid="about-contact"
            title="Talk to us before you commit.">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 border border-slate-200 bg-white p-6">
              <div className="flex-1 text-[14px] text-slate-700 leading-relaxed">
                Institutional pilots, a signed DPA, security questionnaires or an honest answer
                about whether InkPhD fits your project — use the{' '}
                <Link to="/contact" className="underline hover:text-slate-900">contact form</Link>.
                We answer within two working days.
              </div>
              <div className="flex gap-2">
                <Link to="/contact"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-medium border border-slate-300 hover:border-slate-900 transition-colors"
                  data-testid="about-contact-email">
                  <Mail className="w-4 h-4" /> Contact us
                </Link>
                <Link to="/thesis/register"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-medium bg-slate-900 hover:bg-[hsl(212,90%,28%)] text-white transition-colors"
                  data-testid="about-cta-register-bottom">
                  Start free <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </Section>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
